import React from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import Header from '@/components/layout/Header';
import CorporateFooter from '@/components/layout/CorporateFooter';
import SEO from '@/components/common/SEO';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

const sections = [
  {
    title: 'Who we are',
    body: 'CanXP AI is building a Canadian sovereign AI platform for organizations that need private AI, secure knowledge systems, custom-trained models, and stronger control over how AI is deployed.',
  },
  {
    title: 'Why sovereign AI matters',
    body: 'AI is increasingly becoming infrastructure. For Canadian organizations, that raises practical questions about jurisdiction, data residency, access control, and dependency on foreign platforms for sensitive workflows.',
  },
  {
    title: 'What we build',
    body: 'CanXP AI brings together private assistants, secure knowledge bases, sovereign small language models, model training services, and Canadian-hosted deployment options so organizations can adopt AI with more control.',
  },
  {
    title: 'Our platform and model work',
    body: 'CanXP AI is the platform layer, MapleOS is the product environment, MaplePT is the sovereign model family, and model training is a service layer for organizations that need specialized AI behavior.',
  },
  {
    title: 'Canadian infrastructure strategy',
    body: 'We position CanXP around Canadian jurisdiction, Canadian-hosted deployment pathways, and practical sovereign infrastructure for privacy-sensitive and enterprise AI programs.',
  },
  {
    title: 'Press and recognition',
    body: 'CanXP’s public narrative is reinforced by launch coverage, MaplePT coverage, and our broader positioning around sovereign Canadian AI, private deployment, and small language model development.',
  },
];

const relatedLinks = [
  { href: '/sovereign-ai-canada', label: 'Sovereign AI Canada' },
  { href: '/canadian-ai-platform', label: 'Canadian AI Platform' },
  { href: '/ai-model-training', label: 'AI Model Training' },
  { href: '/canadian-ai-infrastructure', label: 'Canadian AI Infrastructure' },
  { href: '/maplept', label: 'MaplePT' },
  { href: '/enterprise', label: 'Contact CanXP AI' },
];

export default function About() {
  return (
    <MainLayout>
      <SEO
        title="About CanXP AI — Building Sovereign AI Infrastructure for Canada"
        description="Learn how CanXP AI is building sovereign AI infrastructure for Canada across private AI, secure knowledge, Canadian-hosted deployment, and model training."
        keywords="About CanXP AI, sovereign AI Canada, Canadian AI infrastructure, private AI platform Canada"
        url="/about"
      />

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="container mx-auto max-w-6xl px-4 py-12 md:py-20">
          <section className="mx-auto max-w-4xl text-center">
            <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
              About CanXP AI
            </Badge>
            <h1 className="mt-6 text-4xl font-semibold tracking-tight md:text-6xl">
              Building Canada’s Sovereign AI Future
            </h1>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              CanXP AI is focused on a simple idea: Canadian organizations should be able to deploy advanced AI without
              giving up control over jurisdiction, infrastructure, private knowledge, and long-term model ownership.
            </p>
          </section>

          <section className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {sections.map((section) => (
              <Card key={section.title} className="border-border/70 bg-background/80">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-semibold tracking-tight">{section.title}</h2>
                  <p className="mt-4 text-sm leading-7 text-muted-foreground">{section.body}</p>
                </CardContent>
              </Card>
            ))}
          </section>

          <section className="mt-16 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-6 md:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">North-star positioning</p>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight">
                  Canada’s sovereign AI platform for private AI, secure knowledge, Canadian jurisdiction, and custom-trained small language models.
                </h2>
                <p className="mt-5 text-sm leading-7 text-muted-foreground md:text-base">
                  That positioning connects the CanXP brand, platform, product layers, model family, and service offering
                  into one coherent enterprise story. It also gives the website a clearer structure for search, sales,
                  and trust-building.
                </p>
              </CardContent>
            </Card>

            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-6 md:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Explore related pages</p>
                <div className="mt-5 space-y-3">
                  {relatedLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="block rounded-xl border border-border/70 bg-background/80 px-4 py-3 text-sm font-medium transition-colors hover:border-primary/30 hover:text-primary"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        </main>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
}
